import { useEffect, useContext, useState } from "react";
import { useSetAtom } from "jotai";
import { useRouter } from "next/router";

import customAxios from "@/custom/custom-axios";
import Modal from "@/components/shared/modal";
import { AuthContext } from "contexts/auth";
import { loadingAtom } from "states/loadingAtom";
import { userDataGlobalAtom } from "@/states/userDataGlobalAtom";
import { isTokenErrorAtom } from "@/states/common/isTokenErrorAtom";
import { adminUser } from "utils/adminUser";
import { debugLog } from "utils/debugLog";
import Loading from "components/shared/loading";
import ButtonV2 from "@/components/shared/buttonV2";

type Props = {
  userType?: "owner" | "crew" | "admin";
  children: React.ReactNode;
};

const Auth: React.FC<Props> = ({ userType, children }) => {
  const router = useRouter();
  const {
    token,
    logout,
    setLogout,
    setUserId,
    setUserType,
    setToken,
    setHasTaskRequest,
    showLoadingGlobal,
    setShowLoadingGlobal,
    setSummaryAccounts,
    setNeedReload
  } = useContext(AuthContext);
  const setShowLoading = useSetAtom(loadingAtom);
  const setUserDataGlobal = useSetAtom(userDataGlobalAtom);
  const setIsTokenError = useSetAtom(isTokenErrorAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);

  //強制ログアウト
  const forceLogout = () => {
    setSummaryAccounts({
      username: "",
      account_type: "normal",
      switchable_users: []
    });
    localStorage.removeItem("token");
    setToken();
  };

  useEffect(() => {
    //アプリのバージョンチェック
    const checkBuildId = async () => {
      const resp = await fetch(`${process.env.APP_FRONT_URL}/BUILD_ID`);
      const text = await resp.text();
      if (localStorage.buildID) {
        if (localStorage.buildID !== text) {
          localStorage.removeItem("buildID");
          localStorage.setItem("buildID", text);
          setNeedReload(true);
        }
      } else {
        localStorage.setItem("buildID", text);
      }
    };

    const authorization = () => {
      const APP_API_PATH = process.env.APP_API_PATH as string;
      const APP_ENV_TEXT = process.env.APP_ENV_TEXT as string;
      if (localStorage.token) {
        customAxios
          .get(`${APP_API_PATH}/val`, {
            headers: { Authorization: `Bearer ${localStorage.token}` }
          })
          .then((res) => {
            if (Number(process.env.MAINTENANCE) === 1) {
              if (adminUser[APP_ENV_TEXT].includes(res.data.userId)) {
                setHasTaskRequest(res.data.has_taskrequest);
                setUserType(res.data.user);
                setToken({
                  headers: { Authorization: `Bearer ${localStorage.token}` }
                });
                setUserId(res.data.userId);
              } else {
                router.push("/maintenance");
              }
            } else {
              debugLog(res.data);
              if (res.data.switchable_users)
                setSummaryAccounts({
                  username: res.data.username,
                  account_type: res.data.is_summary ? "summary" : "child",
                  switchable_users: res.data.switchable_users || []
                });
              setUserType(res.data.user);
              setToken({
                headers: { Authorization: `Bearer ${localStorage.token}` }
              });
              setUserId(res.data.userId);
              setHasTaskRequest(res.data.has_taskrequest);
              setUserDataGlobal(res.data);
            }
            setShowLoadingGlobal(false);
          })
          .catch((e) => {
            console.error("authError", e);
            //MEMO: ネットワークエラーの場合は何もしない
            if (e.response && e.message !== "Network Error") {
              switch (e.response.status) {
                case 429:
                  //アクセス過多エラー発生時
                  setShowLoadingGlobal(true);
                  setTimeout(() => {
                    setShowLoadingGlobal(false);
                  }, 10000);
                  break;
                case 401:
                  //valエラー（アカウント情報取得エラー）
                  setIsModalOpen(true);
                  setShowLoadingGlobal(false);
                  break;
                default:
                  //強制ログアウト
                  forceLogout();
                  router.push(`${userType ? `/${userType}/signin` : "/"}`);
                  setShowLoadingGlobal(false);
              }
            } else {
              setShowLoadingGlobal(false);
            }
          })
          .finally(() => {
            setIsTokenError(false);
          });
      } else {
        //非ログインはログイン画面へリダイレクト
        if (userType) {
          setIsTokenError(true);
          router.push(`/${userType}/signin`);
        }
        setShowLoadingGlobal(false);
      }
    };
    authorization();
    if (process.env.APP_ENV_TEXT !== "local") checkBuildId();
  }, []);

  useEffect(() => {
    if (!logout) return;
    if (token && userType) {
      setShowLoading(true);
      customAxios
        .get(`${process.env.APP_API_PATH}/${userType}/logout`, token)
        .then(() => {
          forceLogout();
          setUserType("");
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setShowLoading(false);
          setLogout(false);
          router.push(`/${userType}/signin`);
        });
    } else {
      router.push("/");
    }
  }, [logout]);

  return (
    <>
      <>{showLoadingGlobal ? <Loading global={true} /> : children}</>
      <>
        {/* トークンが無効な時に表示するモーダル */}
        <Modal
          show={isModalOpen}
          title="ユーザー情報取得エラー"
          showClose={false}
          setShow={setIsModalOpen}
          titleRed
        >
          <div className="px-16">
            <p className="text-md">ログインセッションが切れました。</p>
            <p className="text-md">
              大変お手数ですが、ログイン画面より再度ログインをお願い致します。
            </p>
            <p className="mt-4 text-sm text-cCaptionGray">
              ※「ログイン画面へ」ボタンをクリックしても画面が遷移しない場合は、ページを再読み込みしてから再度ボタンをクリックして下さい。
            </p>
          </div>
          <div className="p-16">
            <ButtonV2
              variant="primary"
              layout="fullWidth"
              click={() => {
                forceLogout();
                router.push(userType ? `/${userType}/signin` : "/");
              }}
            >
              ログイン画面へ
            </ButtonV2>
          </div>
        </Modal>
      </>
    </>
  );
};

export default Auth;
